import React from 'react';
import { Globe, ChevronDown, Loader2 } from 'lucide-react';

interface LanguageAndRegionProps {
  selectedTimezone: number;
  selectedLanguage: string;
  updatingTimezone: boolean;
  onTimezoneChange: (timezone: number) => void;
  onLanguageChange: (language: string) => void;
}

export const LanguageAndRegion: React.FC<LanguageAndRegionProps> = ({
  selectedTimezone,
  selectedLanguage,
  updatingTimezone,
  onTimezoneChange,
  onLanguageChange,
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm mb-6">
      <div className="p-6 border-b">
        <div className="flex items-center gap-3">
          <Globe className="w-6 h-6 text-gray-700" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Idioma y Región</h2>
            <p className="text-sm text-gray-500">
              Configura el idioma y la zona horaria
            </p>
          </div>
        </div>
      </div>
      
      <div className="p-6 space-y-6">
        {/* Timezone selection */}
        <div>
          <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-2">
            Zona horaria
          </label>
          <div className="relative">
            <select
              id="timezone"
              value={selectedTimezone}
              onChange={(e) => onTimezoneChange(parseInt(e.target.value))}
              disabled={updatingTimezone}
              className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 appearance-none"
            >
              <option value="-12">UTC-12:00</option>
              <option value="-11">UTC-11:00</option>
              <option value="-10">UTC-10:00</option>
              <option value="-9">UTC-09:00</option>
              <option value="-8">UTC-08:00</option>
              <option value="-7">UTC-07:00</option>
              <option value="-6">UTC-06:00</option>
              <option value="-5">UTC-05:00</option>
              <option value="-4">UTC-04:00</option>
              <option value="-3">UTC-03:00 (Buenos Aires)</option>
              <option value="-2">UTC-02:00</option>
              <option value="-1">UTC-01:00</option>
              <option value="0">UTC+00:00</option>
              <option value="1">UTC+01:00</option>
              <option value="2">UTC+02:00</option>
              <option value="3">UTC+03:00</option>
              <option value="4">UTC+04:00</option>
              <option value="5">UTC+05:00</option>
              <option value="6">UTC+06:00</option>
              <option value="7">UTC+07:00</option>
              <option value="8">UTC+08:00</option>
              <option value="9">UTC+09:00</option>
              <option value="10">UTC+10:00</option>
              <option value="11">UTC+11:00</option>
              <option value="12">UTC+12:00</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
              <ChevronDown className="h-4 w-4" />
            </div>
          </div>
          {updatingTimezone && (
            <div className="mt-2 flex items-center gap-2 text-blue-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Actualizando zona horaria...</span>
            </div>
          )}
        </div>
        
        {/* Language selection */}
        <div>
          <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-2">
            Idioma
          </label>
          <div className="relative">
            <select
              id="language"
              value={selectedLanguage}
              onChange={(e) => onLanguageChange(e.target.value)}
              className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 appearance-none"
            >
              <option value="es">Español</option>
              <option value="en" disabled>Inglés (próximamente)</option>
              <option value="pt" disabled>Portugués (próximamente)</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
              <ChevronDown className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Más idiomas estarán disponibles próximamente
          </p>
        </div>
      </div>
    </div>
  );
};
